import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import useToggle from 'hooks/useToggle'
import { take, EmptyArray } from 'lib/array/arrayUtils'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import FlashHotelDesktopRateOption from './FlashHotelDesktopRateOption'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileDetails from 'components/AccommodationKit/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import { getAccommodationRoomTileActionsState } from 'components/AccommodationKit/getAccommodationRoomTileActionsState'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import { getDynamicPricingUrgencyLabelData } from '../utils'
import config from 'constants/config'
import { useUrgentOfferRoomDateData } from 'hooks/Offers/useUrgentOfferRoomDateData'
import Group from 'components/utils/Group'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import TextButton from 'LuxKit/Button/TextButton'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Sheet from 'components/utils/Sheet'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import ModalContext from 'contexts/ModalContext'
import { useIntl } from 'react-intl'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(SmallToggleButton)`
  padding: ${rem(12)};
  width: 100%;
  justify-content: center;
`

interface Props {
  offer: App.HotelOffer
  roomTypeId: string
  packageViews: Array<App.OfferPackageOptionView>
  rooms: Array<OfferPageRoom>
  previewMode: boolean
  checkIn?: string
  availableRoomCount?: number
  availableForPurchase?: boolean
  canRedeemLuxPlusBenefits: boolean
  suggestedPackageOption?: {
    option: App.OfferPackageOptionView
    message: string
  }
}

const initialRatesToShow = 4

function FlashHotelDesktopSameRoomOption(props: Props) {
  const {
    offer,
    roomTypeId,
    packageViews,
    rooms,
    previewMode,
    checkIn,
    availableRoomCount,
    availableForPurchase,
    canRedeemLuxPlusBenefits,
    suggestedPackageOption,
  } = props

  const offerId = offer.id
  const isMultiRoom = rooms.length > 1
  const roomCount = rooms.length

  const cachedRoomDateData = useUrgentOfferRoomDateData(offerId, roomTypeId)

  const pkg = packageViews[0].package

  const liveRoomsBeforeSurcharge = useMemo(() => getDynamicPricingUrgencyLabelData(cachedRoomDateData, packageViews[0].availableRooms), [cachedRoomDateData, packageViews])
  const displayedRoomsBeforeSurcharge = useMemo(() => {
    if (!!liveRoomsBeforeSurcharge && config.BRAND === 'luxuryescapes') {
      return liveRoomsBeforeSurcharge
    }
    return undefined
  }, [liveRoomsBeforeSurcharge])
  const intl = useIntl()
  const contactContext = useContext(ContactContext)
  const { currentRegionCode } = useContext(GeoContext)
  const showModal = useContext(ModalContext)
  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)

  const [showingMoreRates, showMoreRates] = useToggle()

  const showMoreOptionsText = showingMoreRates
    ? intl.formatMessage({
        id: '5hFsWv4tpP',
        defaultMessage: 'Show less options',
        description: 'Button text to show less options',
      })
    : intl.formatMessage({
        id: 'CrbvwwjtW2',
        defaultMessage: 'Show more options',
        description: 'Button text to show more options',
      })

  // Availability calculations
  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const isAllRoomsAvailable = packageViews.every(option => option.availableRooms && option.availableRooms >= roomCount)
  const isAllRoomsHaveAvailabilityData = packageViews.every(option => typeof option.availableRooms === 'number')
  // Use explicit === 0 check to distinguish "sold out" (0) from "loading" (undefined)
  const isSoldOut = !!checkIn && packageViews.every(option => option.availableRooms === 0)
  const hasExceededCapacity = packageViews.every(option => option.hotelAvailable && !option.capacityValid)
  const hasHotelMemberPrice = packageViews.some(option => option.hotelMemberPrice > 0)

  const shouldPreventMultipleRoomsBooking = roomCount > 2 && canRedeemLuxPlusBenefits && hasHotelMemberPrice

  const availablePackageViews = useMemo(() => packageViews.filter(view => view.capacityValid && view.hotelAvailable), [packageViews])
  /**
   * Preview is used only by content people.
   * We don't want to show unavailable rateOptions to the user.
   */
  const rateOptionViews = previewMode ? packageViews : availablePackageViews

  // Compute the state for AccommodationRoomTileActions
  // Don't show error messages when phone booking is required
  const actionsState = useMemo(() => {
    if (shouldPreventMultipleRoomsBooking) {
      return undefined
    }
    return getAccommodationRoomTileActionsState({
      availableForPurchase,
      roomFitsCapacity,
      datesAvailable,
      isMultiRoom,
      checkIn,
      isSoldOut,
      hasExceededCapacity,
      isAllRoomsAvailable,
      isAllRoomsHaveAvailabilityData,
    })
  }, [
    availableForPurchase,
    checkIn,
    datesAvailable,
    hasExceededCapacity,
    isAllRoomsAvailable,
    isAllRoomsHaveAvailabilityData,
    isMultiRoom,
    isSoldOut,
    roomFitsCapacity,
    shouldPreventMultipleRoomsBooking,
  ])

  const offerPageDispatch = useOfferPageDispatch()

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, pkg.roomType])

  const handleChangeDatesClick = useCallback(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const onChangeOccupancyClick = useCallback(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const onMultiRoomModeClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleMultiRoomMode, multiRoomMode: 'individual' })
  }, [offerPageDispatch])

  const onCallbackButtonClick = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS" />)
  }, [showModal])

  const croPopularOptionBookingRemaining = !!useOptimizelyExperiment(OptimizelyExperiments.croPopularOptionBookingRemaining, !!availableRoomCount && !!checkIn)

  if (!pkg.roomType) { return null }

  const availabilityText = NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)
    ? intl.formatMessage({
        id: 'xhccxef8II',
        defaultMessage: '7 days a week from 9am to 7pm',
        description: 'Availability text for regions without 24/7 support',
      })
    : intl.formatMessage({
        id: 'D0hdUVfxZZ',
        defaultMessage: '24 hours, 7 days',
        description: 'Availability text for 24/7 support',
      })

  // Phone booking banner for special case
  const phoneBookingBanner = shouldPreventMultipleRoomsBooking ? (
    <Sheet background="neutral-seven" padding={20}>
      <Group direction="vertical" gap={8}>
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'vZMdvugrk6',
              defaultMessage: 'Ready to book? Call us to complete your booking',
              description: 'Heading for phone booking banner',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage(
              {
                id: 'Cu4ixcSrLr',
                defaultMessage: 'Our team are available {availability}',
                description: 'Text about team availability',
              },
              {
                availability: availabilityText,
              },
            )}
          </BodyText>
        </Group>
        <TextButton startIcon={<LinePhoneIcon />} kind="primary" href={`tel:${contactContext.defaultContact.number}`} fit="flex">
          {intl.formatMessage(
            {
              id: 'vAMc14bvA3',
              defaultMessage: 'Call {phoneNumber}',
              description: 'Button text to call phone number',
            },
            {
              phoneNumber: contactContext.defaultContact.humanReadable,
            },
          )}
        </TextButton>
        {callbacksFeatureEnabled && (
          <TextButton kind="secondary" fit="flex" onClick={onCallbackButtonClick}>
            {intl.formatMessage({
              id: 'Nl04pUy6eH',
              defaultMessage: 'Request a callback',
              description: 'Button text to request a callback',
            })}
          </TextButton>
        )}
      </Group>
    </Sheet>
  ) : null

  return (
    <Pane border="neutral-five" borderRadius="S" data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage
          roomPackage={pkg}
        />
        <AccommodationRoomTileDetails
          roomPackage={pkg}
          urgencyLabels={<>
            {!datesAvailable && <UrgencyLabel variant="critical2">
              {intl.formatMessage({
                id: 'xG+JfZMoyZ',
                defaultMessage: 'SOLD OUT',
                description: 'Label indicating sold out status',
              })}
            </UrgencyLabel>}
            <BookingsCountLabel bookings={pkg.roomType.stats.booked} threshold="room" />
            {checkIn && !croPopularOptionBookingRemaining && !displayedRoomsBeforeSurcharge && <RoomsRemainingLabel roomCount={packageViews[0].availableRooms ?? 0} threshold="rate" />}
            {checkIn && croPopularOptionBookingRemaining && !displayedRoomsBeforeSurcharge && availableRoomCount && <OfferUrgencyRoomCount availableRoomCount={availableRoomCount} /> }
          </>}
          actions={availableForPurchase && datesAvailable && roomFitsCapacity && <CSSBreakpoint min="desktop">
            <TripGuard>
              <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
            </TripGuard>
          </CSSBreakpoint>}
          roomCount={rooms.length}
        >
          <AccommodationRoomTileActions
            roomPackage={pkg}
            state={actionsState}
            onChangeOccupancyClick={onChangeOccupancyClick}
            onChangeDatesClick={handleChangeDatesClick}
            onMultiRoomModeClick={onMultiRoomModeClick}
          />
          {phoneBookingBanner}
        </AccommodationRoomTileDetails>
      </AccommodationRoomTile>
      <RateSection>
        {take(rateOptionViews, showingMoreRates ? 100 : initialRatesToShow).map((view, index) => <FlashHotelDesktopRateOption
          key={view.package.uniqueKey}
          roomsBeforeSurcharge={displayedRoomsBeforeSurcharge}
          offer={offer}
          optionNumber={index + 1}
          packageView={view}
          showOptionLabel={rateOptionViews.length > 1}
          suggestedPackageMessage={suggestedPackageOption?.option.package.uniqueKey === view.package.uniqueKey ? suggestedPackageOption.message : undefined}
        />)}
        {rateOptionViews.length > initialRatesToShow &&
          <ShowMoreContent
            on={showingMoreRates}
            onClick={showMoreRates}
            size="small"
          >
            {showMoreOptionsText}
          </ShowMoreContent>}
      </RateSection>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms ?? EmptyArray,
  checkIn: state.checkInDate,
  previewMode: appState.system.previewMode,
  availableForPurchase: isAvailableForPurchase(state, appState),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
  open: !!state.activeFilter,
}), FlashHotelDesktopSameRoomOption, preventRenderWithOpenFilter)
